<template>
  <div class="rd-screen">
    <report-tools :copy="hasData" @copy="copyTable">
      <report-checklist-menu
        v-model="selectedCategories"
        :label="$t('report_group.categories')"
        icon="mdi-view-grid-plus-outline"
        :items="categoryItems"
      ></report-checklist-menu>
    </report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!hasData"
      icon="mdi-view-grid-plus-outline"
      :title="$t('no_data_available')"
      :text="$t('report_desktop.empty_hint')"
    ></report-state>
    <report-state
      v-else-if="!shownCategories.length"
      icon="mdi-view-grid-plus-outline"
      :title="$t('report_group.no_categories')"
    ></report-state>

    <!-- Uvek u stranici: štampa i Excel starog taba čitaju red za štampu iz ove tabele (ref mealsTable). -->
    <div v-show="showMatrix" class="ui-card rd-card gm-scroll">
      <report-desktop-grouped-matrix
        ref="mealsTable"
        :loading="loading"
        :daily-data="dailyData"
        :meal-categories="mealCategories"
        :category-labels="categoryLabels"
        :selected-shift="selectedShifts"
        :selected-categories="selectedCategories"
        :date-array="dateArray"
      ></report-desktop-grouped-matrix>
    </div>
  </div>
</template>

<script>
import CompanyReportTabGroupedShiftMeals from "@/components/company/reports/tabs/CompanyReportTabGroupedShiftMeals.vue";
import hasShiftBinding from "@/components/report/shared/hasShiftBinding";
import hasShiftSubtitle from "@/components/report/shared/hasShiftSubtitle";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportChecklistMenu from "@/components/report/desktop/ReportChecklistMenu.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import ReportDesktopGroupedMatrix from "@/components/report/desktop/screens/group/ReportDesktopGroupedMatrix.vue";

/**
 * Grupe jela firme tipa "grupa" na računaru: za svaki dan perioda koliko je obroka po grupi jela i smeni. Firme,
 * smene i period dolaze iz zajedničke trake, a grupe jela se biraju u alatima. Nasleđuje učitavanje, štampu i
 * Excel iz starog taba; tabela je ista kao u "Jela iz grupa" izveštaja restorana (ReportDesktopGroupedMatrix).
 */
export default {
  name: "CompanyDesktopGroupGrouped",
  extends: CompanyReportTabGroupedShiftMeals,
  mixins: [hasShiftBinding, hasShiftSubtitle],
  components: { ReportChecklistMenu, ReportDesktopGroupedMatrix, ReportState, ReportTools },
  computed: {
    hasData() {
      return this.rows.length > 0;
    },
    shownCategories() {
      return this.selectedCategories.filter((category) => this.mealCategories.includes(category));
    },
    showMatrix() {
      return !this.loading && this.hasData && this.shownCategories.length > 0;
    },
    kpis() {
      const total = this.rows
        .filter((row) => this.selectedShifts.includes(row.shift) && this.selectedCategories.includes(row.category_id))
        .reduce((sum, row) => sum + (row.quantity || 0), 0);
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: total, tone: "inverse" },
        { key: "groups", label: this.$t("report_group.categories"), value: this.shownCategories.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.hasData ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const table = this.$refs.mealsTable;
      if (!table) {
        return;
      }
      const shifts = table.shiftList;
      const columns = table.filteredMealCategories.flatMap((category) => [
        ...shifts.map((shift) => `${category}_${shift}`),
        `${category}_total`,
      ]);
      const head = [
        this.$t("day"),
        ...table.filteredMealCategories.flatMap((category) => [
          ...shifts.map((shift) => `${table.categoryText(category)} ${shift}`),
          `${table.categoryText(category)} Σ`,
        ]),
        this.$t("total"),
      ];
      const rows = table.groupedData.map((row, index) => [
        row.isTotal ? this.$t("total") : table.dayText(index),
        ...columns.map((key) => row[key] || 0),
        row.grandTotal || 0,
      ]);
      const copied = await copyText(rowsToTsv(head, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
